import { ThemeOptions } from '@mui/material/styles';

// Base mandant themes
const baseThemes: { [key: string]: ThemeOptions } = {
  levelnine: {
    palette: {
      mode: 'light',
      primary: {
        main: '#1bac66',
        dark: '#1bac66',
        contrastText: '#fff',
      },
      secondary: {
        main: '#383637',
        dark: 'rgba(0,0,0,0.87)',
      },
      text: {
        primary: 'rgba(0,0,0,0.87)',
        secondary: 'rgba(0,0,0,0.6)',
        disabled: 'rgba(0,0,0,0.38)',
      },
      stepperBg: {
        default: '#1B1C4D',
        muted: '#bdbdbd',
        paper: '#FFFFFF',
      },
      inputFields: {
        bgColor: '#f0f0f0',
        main: '#333333',
      },
      statusColors: {
        alert: { main: '#663c00', bgColor: '#fff4e5' },
        info: { main: '#537e94', bgColor: '#e5f6fd' },
      },
      accessibility: {
        button: {
          main: '#000000',
          contrastText: '#FFFFFF',
          border: '#000000',
        },
        link: {
          main: '#000000',
          underline: true,
        },
      },
    },
    typography: {
      fontFamily: 'Open Sans',
    },
  },
  hallesche: {
    palette: {
      mode: 'light',
      primary: {
        main: '#be0d3e',
        dark: '#be0d3e',
        contrastText: '#fff',
      },
      secondary: {
        main: '#004767',
        dark: 'rgba(0,0,0,0.87)',
      },
      text: {
        primary: 'rgba(0,0,0,0.87)',
        secondary: 'rgba(0,0,0,0.6)',
        disabled: 'rgba(0,0,0,0.38)',
      },
      customInfoIconColor: {
        main: 'white',
        text: '#004767',
        textPersonHeader: '#be0d3e',
      },
      inputFields: {
        bgColor: '#f0f0f0',
        main: '#333333',
      },
      statusColors: {
        alert: { main: '#663c00', bgColor: '#fff4e5' },
        info: { main: '#537e94', bgColor: '#e5f6fd' },
      },
      stepperBg: {
        default: '#1B1C4D',
        muted: '#bdbdbd',
        paper: '#FFFFFF',
      },
      accessibility: {
        button: {
          main: '#000000',
          contrastText: '#FFFFFF',
          border: '#000000',
        },
        link: {
          main: '#000000',
          underline: true,
        },
      },
    },
    typography: {
      fontFamily: 'Ubuntu',
    },
  },
  sdk: {
    palette: {
      mode: 'light',
      primary: {
        main: '#00797F',
        dark: '#00797F',
        contrastText: '#fff',
      },
      secondary: {
        main: '#531F75',
        dark: '#531F75',
      },
      text: {
        primary: '#1A1A1A',
        secondary: '#1A1A1A',
        disabled: '#1A1A1A',
      },
      inputFields: {
        bgColor: '#f0f0f0',
        main: '#333333',
      },
      statusColors: {
        alert: { main: '#663c00', bgColor: '#fff4e5' },
        info: { main: '#537e94', bgColor: '#e5f6fd' },
      },
      stepperBg: {
        default: '#1B1C4D',
        muted: '#bdbdbd',
        paper: '#FFFFFF',
      },
      accessibility: {
        button: {
          main: '#000000',
          contrastText: '#FFFFFF',
          border: '#000000',
        },
        link: {
          main: '#000000',
          underline: true,
        },
      },
    },
    typography: {
      fontFamily: 'Helvetica-Neue',
    },
  },
  vmk: {
    palette: {
      mode: 'light',
      primary: {
        main: '#024589',
        dark: '#024589',
        contrastText: '#fff',
      },
      secondary: {
        main: '#9db81d',
        dark: '#9db81d',
      },
      text: {
        primary: 'rgba(0,0,0,0.87)',
        secondary: 'rgba(0,0,0,0.6)',
        disabled: 'rgba(0,0,0,0.38)',
      },
      inputFields: {
        bgColor: '#f0f0f0',
        main: '#333333',
      },
      statusColors: {
        alert: { main: '#663c00', bgColor: '#fff4e5' },
        info: { main: '#537e94', bgColor: '#e5f6fd' },
      },
      stepperBg: {
        default: '#1B1C4D',
        muted: '#bdbdbd',
        paper: '#FFFFFF',
      },
      accessibility: {
        button: {
          main: '#000000',
          contrastText: '#FFFFFF',
          border: '#000000',
        },
        link: {
          main: '#000000',
          underline: true,
        },
      },
    },
    typography: {
      fontFamily: 'Ubuntu',
    },
  },
};

// Accessibility overrides - these colors will replace the base theme colors when accessibility mode is enabled
const accessibilityOverrides = {
  primary: {
    main: '#000000',
    dark: '#000000',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#000000',
    dark: '#000000',
  },
  text: {
    primary: '#000000',
    secondary: '#000000',
    disabled: '#747474',
  },
  inputFields: {
    bgColor: 'transparent',
    main: '#000000',
  },
  statusColors: {
    alert: { main: '#000000', bgColor: '#FFFFFF' },
    info: { main: '#000000', bgColor: '#FFFFFF' },
  },
  stepperBg: {
    default: '#000000',
    muted: '#747474',
    paper: '#FFFFFF',
  },
};

// Function to get the theme based on mandant and accessibility mode
export const getMandantTheme = (mandant: string, isColorBlindMode: boolean): ThemeOptions => {
  const baseTheme = baseThemes[mandant] || baseThemes['levelnine'];
  
  if (!isColorBlindMode) {
    return baseTheme;
  }

  // Apply accessibility overrides
  return {
    ...baseTheme,
    palette: {
      ...baseTheme.palette,
      ...accessibilityOverrides,
      // Preserve mandant-specific customInfoIconColor if it exists
      ...(baseTheme.palette?.customInfoIconColor && {
        customInfoIconColor: {
          main: '#FFFFFF',
          text: '#000000',
          textPersonHeader: '#000000',
        },
      }),
    },
  };
};

// Legacy export for backward compatibility
export default baseThemes;